import Vue, { VNode } from "vue"
import Router, { RouteConfig } from "vue-router"

import AgencyDetailPage from "@bgov/agency-page-vue"
import BudgetAppropriations from "@bgov/budget-appropriations-vue"
import Companies from "@bgov/company-page-vue"
import CongressionalCommitteePage from "@bgov/congressional-committee-vue"
import GcExclusives from "@bgov/gc-exclusives-vue"
import Help from "@bgov/help-page-vue"
import NewsPages from "@bgov/news-pages-vue"
import Opportunities from "@bgov/opportunities-vue"
import OpportunitySearch from "@bgov/opportunity-search-vue"
import StateLegislation from "@bgov/state-legislation-vue"
import GcComboSearch from "@bgov/gc-combo-search"
import ContractVehicles from "@bgov/contract-vehicles"
import MyBgov from '@bgov/my-bgov-vue';

import { createRoutes as createAuthRoutes } from "@bgov/authentication-pages-vue"

// Please keep this list in alphabetical order
let appRoutes: RouteConfig[] = [
  ...AgencyDetailPage,
  ...BudgetAppropriations,
  ...Companies,
  ...CongressionalCommitteePage,
  ...GcExclusives,
  ...Help,
  ...NewsPages,
  ...Opportunities,
  ...OpportunitySearch,
  ...StateLegislation,
  ...GcComboSearch,
  ...ContractVehicles,
  ...MyBgov,
  {
    path: "/error-test",
    component: () => import("./ErrorTest.vue"),
  },
]

/*
 * By default all routes in our SPA should be under the `/next` URL namespace.
 * Instead of making every project make sure to do that, we'll do the check for
 * all routes here and prepend the appropriate URL path.  Any routes we want
 * to behave differently (like auth pages) should be directly inserted into
 * the route config below instead of in the `appRoutes` array above.
 */
appRoutes.forEach(route => {
  if (route.path.indexOf("/next") != 0) {
    route.path = `/next${route.path}`
  }
})

const router = new Router({
  mode: "history",
  routes: appRoutes,
})

let myBgovIsInitialized = false

router.afterEach(currentRoute => {
  if (!myBgovIsInitialized && !currentRoute.meta.skipAuthentication) {
    router.app.$myBgov("mount", document.getElementById("myBgovMountPoint"))
    myBgovIsInitialized = true
  }
})

// Auth pages and a couple other things are served at top-level URL namespace
router.addRoutes(createAuthRoutes(router))

/*
  * Pokemon routes (gotta catch em all!) for any unhandled routes.  This generally
  * for pages in legacy apps.  Vue will take over the routing and not let them
  * correctly navigate to the server. If we hit one of these, do a full page reload
  * to trigger a round trip.  This will eventually go away as we move most of the
  * app over.
  */
const LEGACY_PATH_PREFIXES = [
  // bgov-core
  "/core",
  "/users",
  "/static/service",
  "/us_agency_contacts",
  "/directories/bgov_exclusives",
  "/directories/contributions_search",
  "/us_government_affairs",
  // bgov-spend
  "/spend/",
  // bgov-legislation
  "/us_committees",
  "/us_legislation",
  "/caucuses",
  "/us_regulation",
  "/home",
  // bgov-web
  "/states",
  "/reports",
  "/districts",
  "/federal_budgets",
]

router.addRoutes([
  {
    path: "/",
    redirect: "/core/home",
  },
  {
    path: "*",
    meta: {
      layout: "default",
    },
    component: () => import("./NotFound.vue"),
    beforeEnter: (to, from, next) => {
      let halt = false
      LEGACY_PATH_PREFIXES.forEach(legacyPath => {
        if (halt) return

        if (to.fullPath.startsWith(legacyPath)) {
          if (process.env.NODE_ENV === "production") {
            // eslint-disable-next-line
            console.log(
              `Requested path "${
                to.fullPath
              }" matched legacy prefix "${legacyPath}". Performing full page refresh`
            )
            window.location.reload()
          } else {
            // eslint-disable-next-line
            console.error(`Requested path "${to.fullPath}" is a legacy appliction route (matched prefix was "${legacyPath}") and cannot be loaded in development mode.`)
          }
          halt = true
        }
      })

      if (halt) {
        next(false)
      } else {
        // If it's not a page that exists on one of the other apps, then it doesn't exist and we should
        // show the Not Found page.
        next(vue => {
          vue.$errorRenderer.notFound()
        })
      }
    },
  },
])

export default router
